import { Inject, Injectable } from '@angular/core';
import {
    MutableTimestamp,
    Operation,
    State,
    ClockService,
    Timestamp,
    dbVersion,
    APP_VERSION,
    Synced,
    PriceVersion,
    ProtocolEmailStatus,
} from '@icc/b2b/shared';
import { Selector, Store } from '@ngrx/store';

import { io, Socket } from 'socket.io-client';
import {
    deletedDocumentsOnWorker,
    loadedDocumentsFromDB,
    mergeOps,
    opsSyncedOnWorker,
    updatedDocumentsOnWorker,
    updatedSyncedStateOnWorker,
} from './+state/sales-documents.actions';

import { v4 as uuidv4 } from 'uuid';
import { ConfigService, logger } from '@icc/shared';
import { HttpClient } from '@angular/common/http';
import { isObject, StatsService, ToastService } from '@icc/shared/helpers/b2b';
import { TranslateService } from '@ngx-translate/core';
import { IDBServiceBrowser } from './idb.service.browser';
import { catchError, filter, firstValueFrom, of, skipWhile, Subject, tap, timeout, TimeoutError } from 'rxjs';
import {
    WorkerRequestMessage,
    WorkerResponseMessage,
} from './synchronization/worker-message.model';
import { LoaderService } from './loader.service';
import { setContext } from '@sentry/browser';
import { NgxPermissionsService } from 'ngx-permissions';
import { MerkleTrie } from 'libs/b2b/shared/src/lib/sync/merkle';
import { PriceVersionService } from './price-version-selector/price-version.service';
import { getSynced } from './+state/sales-documents.selectors';

@Injectable({
    providedIn: 'root',
})
export class SyncService {
    socket!: Socket;
    worker!: Worker | SharedWorker;
    isSharedWorker = false;
    machine = localStorage.getItem('id') || '';

    protocolEmailStatus$ = new Subject<ProtocolEmailStatus>();

    placedOrder$ = new Subject<{
        status: 'success' | 'error' | 'exception';
        documentId?: string;
        documentNumber?: string;
        error?: string;
        messages?: string[];
        hasIntegration?: boolean;
    }>();

    synchronizationInProgress = false;
    event = null;
    synchronizationOnWorker = false;

    currentDocumentsIds: string[] = [];
    loadingDocuments: string[] = [];
    loadedDocuments: string[] = [];

    opsToSendToWorker: Operation[] = [];
    synced$ = this.store.select(getSynced);

    constructor(
        private store: Store<State>,
        private http: HttpClient,
        private toastService: ToastService,
        private translateService: TranslateService,
        private configService: ConfigService,
        private idbService: IDBServiceBrowser,
        @Inject(APP_VERSION) private appVersion: string,
        private loaderService: LoaderService,
        private permissionsService: NgxPermissionsService,
        private priceVersionService: PriceVersionService,
        private statsService: StatsService,
    ) {}

    start(userId: number | string) {
        const envDbVersion = this.configService.config?.env?.iccDbVersion || 1;
        (window as any).envDbVersion = envDbVersion;
        (window as any).USER_ID = '' + userId;
        (window as any).dbVersion = '' + dbVersion;
        this.machine =
            localStorage.getItem('id+' + userId + '+' + dbVersion + '+' + envDbVersion) || '';
        if (!this.machine) {
            const clientId = this.makeClientId();
            localStorage.setItem('id+' + userId + '+' + dbVersion + '+' + envDbVersion, clientId);
            this.machine = clientId;
        }
        setContext('instance', {
            machine: this.machine,
        });

        this.connectSocket(userId, envDbVersion);

        this.syncStats();

        window.onbeforeunload = () => {
            if (ClockService.getClock()) {
                this.idbService.saveSyncTrie(
                    ClockService.serializeClock(ClockService.getClock()),
                    ClockService.updatedGraphs
                );
            }
        };

        document.onvisibilitychange = () => {
            if (ClockService.getClock()) {
                this.idbService.saveSyncTrie(
                    ClockService.serializeClock(ClockService.getClock()),
                    ClockService.updatedGraphs
                );
            }
        };

        return this.machine;
    }

    async startDocumentSync(openedDocumentId: string) {
        const serializedClock = await this.idbService.getSyncTrie();
        if (serializedClock) {
            const clock = ClockService.deserializeClock(serializedClock);
            if (clock?.timestamp?.node() !== this.machine) {
                clock?.timestamp?.setNode(this.machine);
            }
            ClockService.setClock(clock);
        } else {
            ClockService.setClock(ClockService.makeClock(new MutableTimestamp(0, 0, this.machine)));
        }

        this.createWorker(openedDocumentId);
    }

    private connectSocket(userId: number | string, envDbVersion: number) {
        this.socket = io({
            path: '/api/socket.io',
            autoConnect: false,
            withCredentials: true,
            query: {
                worker: 'false',
                token: '',
                machine: this.machine,
                sync: 'true',
                dbVersion: String(dbVersion),
                appVersion: this.appVersion,
                envDbVersion: String(envDbVersion),
                activePanel: this.configService.activePanel,
            },
            transports: ['websocket'],
        });
        this.socket.auth = {
            userId,
        };

        this.socket.on('connect', () => {
            logger.log(
                'Connected SID: %s Machine: %s UserId: %s',
                this.socket.id,
                this.machine,
                userId
            );

            this.socket.emit('identity', 0, (response: any) => logger.log('Identity:', response));
        });
        this.socket.connect();
        this.socket.on('exception', (data: any) => {
            logger.log('exception', data);
        });
        this.socket.on('disconnect', () => {
            logger.log('Disconnected');
        });

        this.socket.on('permissionsUpdated', (options) => {
            logger.log('permissionsUpdated', options);
            this.updatePermissions(options);
        });

        this.socket.on('updatedPriceVersions', (priceVersions: PriceVersion[]) => {
            logger.log('updatedPriceVersions');
            this.priceVersionService.updatePriceVersions(priceVersions);
        });

        this.socket.on('incorrectUser', () => {
            window.location.href = '/iccadmin?redirect=' + window.location.pathname;
        });

        this.socket.on('placedOrder', (message) => {
            this.processPlacedOrderMessage({
                type: 'placedOrder',
                data: message,
            });
        });

        this.socket.on('protocolEmailStatus', (data: ProtocolEmailStatus) => {
            this.protocolEmailStatus$.next(data);
        });
    }

    private createWorker(openedDocumentId: string) {
        if (typeof Worker !== 'undefined') {
            if (typeof SharedWorker !== 'undefined') {
                this.worker = new SharedWorker(new URL('./sync.worker', import.meta.url), {
                    name: 'sync-worker',
                    type: 'module',
                });
                this.isSharedWorker = true;
            } else {
                this.worker = new Worker(new URL('./sync.worker', import.meta.url), {
                    name: 'sync-worker',
                    type: 'module',
                });
                this.isSharedWorker = false;
            }
            this.synchronizationOnWorker = true;
            if (this.isSharedWorker && this.worker instanceof SharedWorker) {
                this.worker.port.onmessage = ({ data }) => {
                    logger.log(`page got message:data`, data.type);
                    this.receiveWorkerMessage(data);
                };
            } else if (this.worker instanceof Worker) {
                this.worker.onmessage = ({ data }) => {
                    logger.log(`page got message:data`, data.type);
                    this.receiveWorkerMessage(data);
                };
            }

            this.currentDocumentsIds = openedDocumentId ? [openedDocumentId] : [];

            this.sendMessageToWorker({
                type: 'run',
                data: {
                    activePanel: this.configService.activePanel,
                    user: this.configService.config?.userData?.user,
                    machine: this.machine,
                    dbVersion,
                    envDbVersion: this.configService.config?.env?.iccDbVersion || 1,
                    appVersion: this.appVersion,
                    statusSystems: this.configService.config?.userData?.statusSystems,
                    sentryDsn: this.configService.config?.env?.sentryDsn,
                    sentryEnv: this.configService.config?.env?.sentryEnv || 'dev',
                    openedDocumentId,
                    recentlyViewedDocumentsPeriod: this.configService.config?.env?.recentlyViewedDocumentsPeriod,
                },
            });
        } else {
            this.toastService.add({
                title: this.translateService.instant('INTERFACE|Błąd synchronizacji'),
                body: this.translateService.instant(
                    'INTERFACE|Twoja przeglądarka nie obsługuje synchronizacji'
                ),
                life: 10000,
                severity: 'error',
            });
            throw new Error('Web worker is not supported');
        }
    }

    sendOperationsToWorker({
        ops,
        staleDocs,
        graphsToSend,
    }: {
        ops: Operation[];
        staleDocs: Map<string, number>;
        graphsToSend: Map<string, number>;
    }) {
        if (this.synchronizationOnWorker) {
            const opsToSend: Operation[] = [];
            const docsSet = new Set<string>();

            for (const [docId, hash] of graphsToSend) {
                if (hash === ClockService.getClock().merklesMap[docId]?.hash) {
                    docsSet.add(docId);
                }
            }

            this.opsToSendToWorker.push(...ops);

            for (const op of this.opsToSendToWorker) {
                if (
                    ('docId' in op && op.docId && docsSet.has(op.docId)) ||
                    ('buyDocId' in op && op.buyDocId && docsSet.has(op.buyDocId))
                ) {
                    opsToSend.push(op);
                } else {
                    // console.log('SKIP', `${op.docId},${'buyDocId' in op ? op.buyDocId : '0'}/${op.timestamp}/${op.type} ${graphsToSend.get(op.docId)}/${ClockService.getClock().merklesMap[op.docId]?.hash} ${'buyDocId' in op && graphsToSend.get(op.buyDocId || '')}/${'buyDocId' in op && ClockService.getClock().merklesMap[op.buyDocId]?.hash || ''}`);
                }
            }

            const graphsForWorker: Record<string, MerkleTrie> = {};
            const updatedGraphs: Record<string, number> = {};
            const staleDocsToSend: Map<string, number> = new Map();

            let graphsToSendLog = 'graphsToSendLog: ';

            for (const docId of docsSet) {
                graphsForWorker[docId] = ClockService.getClock().merklesMap[docId];
                updatedGraphs[docId] = ClockService.updatedGraphs[docId];
                if (staleDocs.has(docId)) {
                    staleDocsToSend.set(docId, staleDocs.get(docId) || 0);
                }
                graphsToSendLog += `${docId}: ${graphsForWorker[docId].hash}, `;
            }
            if (docsSet.size > 0 || opsToSend.length > 0) {
                // console.log('SEND TO WORKER', Object.keys(updatedGraphs).join(','), Object.keys(graphsForWorker).map(docId => `${docId}: ${graphsForWorker[docId].hash}`).join(', '), graphsToSendLog);
                this.sendMessageToWorker({
                    type: 'emitOperations',
                    data: {
                        ops: opsToSend,
                        graphs: graphsForWorker,
                        updatedGraphs,
                        staleDocs: staleDocsToSend,
                    },
                });
            }

            if (opsToSend.length) {
                this.opsToSendToWorker = this.opsToSendToWorker.filter(
                    (op) => !opsToSend.includes(op)
                );
            }
        }
    }

    sendProcessResultsToWorker(documents: string[], syncKey: number) {
        const graphsForWorker: Record<string, MerkleTrie> = {};
        const updatedGraphs: Record<string, number> = {};

        for (const docId of documents) {
            graphsForWorker[docId] = ClockService.getClock().merklesMap[docId];
            updatedGraphs[docId] = ClockService.updatedGraphs[docId];
        }

        // console.log('local merkles', documents, ClockService.getClock().merklesMap);
        // console.log('SEND TO WORKER OPENED', syncKey, Object.keys(updatedGraphs).join(','), Object.keys(graphsForWorker).map(docId => `${docId}: ${graphsForWorker[docId].hash}`).join(', '));
        this.sendMessageToWorker({
            type: 'processedOperations',
            data: {
                graphs: graphsForWorker,
                updatedGraphs,
                key: syncKey,
            },
        });
    }

    sendMessageToWorker(message: WorkerRequestMessage) {
        if (this.worker) {
            if (this.isSharedWorker && this.worker instanceof SharedWorker) {
                this.worker.port.postMessage(message);
            } else if (this.worker instanceof Worker) {
                this.worker.postMessage(message);
            }
        }
    }

    receiveWorkerMessage(message: WorkerResponseMessage) {
        switch (message.type) {
            case 'placedOrder':
                this.processPlacedOrderMessage(message);
                break;
            case 'processOperationsForOpenedDocuments': {
                const statusSystems = this.configService.config?.userData?.statusSystems;

                // console.log('PROCESS OPS', message.data.key, message.data.ops.map(op => `${op.docId}/${'buyDocId' in op ? op.buyDocId : '0'}/${op.timestamp}/${op.type}`).join(', '));
                this.store.dispatch(
                    mergeOps({
                        operations: message.data.ops,
                        statusSystems,
                        syncKey: message.data.key,
                    })
                );
                break;
            }
            case 'syncedOperations':
                this.store.dispatch(
                    opsSyncedOnWorker({
                        ops: message.data.ops,
                    })
                );
                break;
            case 'updatedDocuments':
                this.store.dispatch(
                    updatedDocumentsOnWorker({
                        updates: message.data,
                    })
                );
                break;
            case 'deletedDocuments':
                this.store.dispatch(
                    deletedDocumentsOnWorker({
                        updates: message.data,
                    })
                );
                break;
            case 'updatedClock':
                try {
                    Timestamp.recv(
                        ClockService.getClock(),
                        Timestamp.parse(message.data.timestamp),
                        true
                    );
                } catch (err) {
                    if (err.name === 'ClockDriftError') {
                        this.toastService.add({
                            title: this.translateService.instant('INTERFACE|Błąd synchronizacji'),
                            body: this.translateService.instant(
                                'INTERFACE|Czas na twoim urządzeniu jest niezgodny z czasem serwera. Zaktualizuj czas na twoim urządzeniu.'
                            ),
                            life: 1000,
                            sticky: true,
                            severity: 'error',
                        });
                        logger.error(err);
                    } else {
                        throw err;
                    }
                }
                break;
            case 'updatedSyncedState':
                this.store.dispatch(
                    updatedSyncedStateOnWorker({
                        synced: message.data.synced,
                        staleDocs: message.data.staleDocs,
                        changedDocs: message.data.changedDocs,
                    })
                );
                break;
            case 'error':
                if (message.data.error === 'INCORRECT_USER') {
                    logger.warn(message.data.message);
                } else {
                    logger.error(message.data.message);
                }
                if (message.data.error === 'INCORRECT_USER') {
                    if (this.worker) {
                        if (this.isSharedWorker && this.worker instanceof SharedWorker) {
                            this.worker.port.close();
                        } else if (this.worker instanceof Worker) {
                            this.worker.terminate();
                        }
                    }
                    window.location.href = '/iccadmin?redirect=' + window.location.pathname;
                } else if (message.data.error === 'INCORRECT_VERSION') {
                    window.location.href = '/iccadmin?redirect=' + window.location.pathname;
                    this.toastService.add({
                        title: this.translateService.instant('INTERFACE|Nieaktualna aplikacja'),
                        body: this.translateService.instant(
                            'INTERFACE|Uruchomiona wersja aplikacji jest nieaktualna.'
                        ),
                        life: 1000,
                        severity: 'error',
                    });
                } else if (message.data.error === 'ClockDriftError') {
                    this.toastService.add({
                        title: this.translateService.instant('INTERFACE|Błąd synchronizacji'),
                        body: this.translateService.instant(
                            'INTERFACE|Czas na twoim urządzeniu jest niezgodny z czasem serwera. Zaktualizuj czas na twoim urządzeniu.'
                        ),
                        life: 1000,
                        sticky: true,
                        severity: 'error',
                    });
                } else {
                    this.toastService.add({
                        title: this.translateService.instant('INTERFACE|Błąd synchronizacji'),
                        body: this.translateService.instant(
                            'INTERFACE|Wystąpił błąd podczas synchronizacji.'
                        ),
                        life: 1000,
                        severity: 'error',
                    });
                    this.loaderService.setError(message.data.message, message.data.docIds);
                }
                break;
            case 'message':
                logger.log(message.data.message);
                this.toastService.add({
                    title: this.translateService.instant('INTERFACE|Synchronizacja'),
                    body: message.data.message,
                    life: 1000,
                    severity: 'info',
                });
                break;
        }
    }

    private processPlacedOrderMessage(message: {
        type: 'placedOrder';
        data: {
            documentId: string;
            status: 'error' | 'success' | 'exception';
            error?: string;
            documentNumber?: string;
            messages?: string[];
            hasIntegration?: boolean;
        };
    }) {
        this.placedOrder$.next(message.data);
        if (message.data.hasIntegration && message.data.status !== 'success') {
            if (message.data.messages.length) {
                this.toastService.add({
                    title: this.translateService.instant(
                        'SALES_DOCUMENT|Niepowodzenie składania zamówienia'
                    ),
                    body: this.translateService.instant(
                        'SALES_DOCUMENT|Wystąpiły błędy podczas składania zamówienia: {messages}',
                        { messages: message.data.messages.join('\r\n') }
                    ),
                    life: 5000,
                    severity: 'error',
                });
            } else {
                this.toastService.add({
                    title: this.translateService.instant('SALES_DOCUMENT|Błąd komunikacji'),
                    body: this.translateService.instant(
                        'SALES_DOCUMENT|Brak potwierdzenia zapisu zamówienia {number}, oczekuj na powiadomienie email o stanie transakcji. W przypadku braku powiadomienia w ciągu 24h skontaktuj się ze swoim opiekunem klienta w celu wyjaśnienia sprawy.',
                        { number: message.data.documentNumber }
                    ),
                    life: 5000,
                    severity: 'error',
                });
            }
        } else if (message.data.status === 'success') {
            this.toastService.add({
                title: this.translateService.instant('SALES_DOCUMENT|Złożono zamówienie'),
                body: this.translateService.instant(
                    'SALES_DOCUMENT|Zamówienie nr {number} zostało złożone.',
                    { number: message.data.documentNumber }
                ),
                life: 5000,
                severity: 'success',
            });
        } else if (this.configService.user.orderIntegration !== 'OrdersSynchronization') {
            this.toastService.add({
                title: this.translateService.instant('SALES_DOCUMENT|Nie złożono zamówienia'),
                body: this.translateService.instant(
                    'SALES_DOCUMENT|Składanie zamówienia nr {number} nie powiodło się.',
                    { number: message.data.documentNumber }
                ),
                life: 5000,
                severity: 'error',
            });
        }
    }

    makeClientId() {
        return uuidv4().replace(/-/g, '').slice(-16);
    }

    checkUser(userId: number) {
        return new Promise<boolean>((resolve, reject) => {
            if (!this.socket) {
                resolve(false);
            }
            this.socket
                .emit('checkUser', { userId }, (response) => {
                    if (response.refresh) {
                        window.location.href = '/iccadmin?redirect=' + window.location.pathname;
                        resolve(true);
                    } else {
                        resolve(false);
                    }
                })
                .on('error', (error) => {
                    reject(error);
                });
        });
    }

    updatePermissions(options: { permissions: Record<number, string>; tradeSettings?: any }) {
        if (!isObject(options)) {
            return;
        }
        if (!options.permissions) {
            options.permissions = this.configService.config?.userData?.user?.ActiveScopePermissions;
        }
        if (!options.tradeSettings) {
            options.tradeSettings =
                this.configService.config?.userData?.user?.ActiveCompanyTradeSettings;
        }
        if (options.permissions) {
            this.permissionsService.flushPermissions();
            this.permissionsService.addPermission(Object.values<string>(options.permissions));

            this.sendMessageToWorker({
                type: 'changedPermissions',
                data: null,
            });
        }

        if (options.tradeSettings) {
            this.configService.config.userData.user.ActiveCompanyTradeSettings =
                options.tradeSettings;
            this.configService.config.userData.blockedOrdering = Boolean(
                Number(options.tradeSettings.blockedOrdering)
            );
            this.configService.config.userData.chargeAvailable = Boolean(
                Number(options.tradeSettings.chargeAvailable)
            );
        }
    }

    addCurrentDocumentId(documentId: string) {
        if (documentId && !this.currentDocumentsIds.includes(documentId)) {
            this.currentDocumentsIds.push(documentId);
        }
        this.sendMessageToWorker({
            type: 'changedOpenedDocuments',
            data: {
                documentsIds: this.currentDocumentsIds,
            },
        });

        ClockService.deletedDocs = ClockService.deletedDocs.filter(docId => ! this.currentDocumentsIds.includes(docId));
    }

    setCurrentDocumentsIds(ids: string[]) {
        if (
            this.currentDocumentsIds.length !== ids.length ||
            this.currentDocumentsIds.some((id) => !ids.includes(id)) ||
            ids.some((id) => !this.currentDocumentsIds.includes(id))
        ) {
            this.currentDocumentsIds = ids;
        }
        this.sendMessageToWorker({
            type: 'changedOpenedDocuments',
            data: {
                documentsIds: this.currentDocumentsIds,
            },
        });
        ClockService.deletedDocs = ClockService.deletedDocs.filter(docId => ! this.currentDocumentsIds.includes(docId));
    }

    async loadDocuments(documentsId: string[]) {
        if (documentsId.length === 0) {
            return;
        }
        if (this.loadingDocuments.length && documentsId.every((id) => this.loadingDocuments.includes(id))) {
            return
        }
        this.loadingDocuments.push(...documentsId);
        const ancestors = await this.idbService.getDocumentsByRootId(documentsId);
        const neededDocsIds: string[] = [...documentsId];
        ancestors.forEach((doc) => {
            if (!neededDocsIds.includes(doc.uuid)) {
                neededDocsIds.push(doc.uuid);
            }
        });
        if (neededDocsIds.length === 0) {
            return;
        }
        const partialState = await this.idbService.getMany(neededDocsIds);
        this.store.dispatch(
            loadedDocumentsFromDB({
                updates: partialState,
            })
        );
        this.loadingDocuments = this.loadingDocuments.filter((id) => !documentsId.includes(id));
    }

    async getOrLoadDocument<T>(
        documentId: string,
        selector: (documentId: string) => Selector<any, T>,
        existsFn: (doc: T) => boolean = (doc) => !!doc
    ) {
        const document = await firstValueFrom(this.store.select(selector(documentId)));
        if (!existsFn(document)) {
            await this.loadDocuments([documentId]);
            return firstValueFrom(
                this.store.select(selector(documentId)).pipe(
                    timeout({
                        each: 500,
                    }),
                    skipWhile((doc) => !doc),
                    catchError(() => of(null))
                )
            );
        }
        return document;
    }

    async getOrLoadDocuments<T>(
        documentIds: string[],
        selector: (documentIds: string[]) => Selector<any, T>,
        existsFn: (doc: T) => boolean = (doc) => !!doc
    ) {
        const documents = await firstValueFrom(this.store.select(selector(documentIds)));
        if (!existsFn(documents)) {
            await this.loadDocuments(documentIds);
            return firstValueFrom(
                this.store.select(selector(documentIds)).pipe(
                    timeout({
                        each: 500,
                    }),
                    skipWhile((doc) => !doc),
                    catchError(() => of(null))
                )
            );
        }
        return documents;
    }

    getSynced(documentsIds: string[], syncedDocuments: Map<string, Synced>, online = false) {
        const selectedSynced = documentsIds.map((doc) => syncedDocuments?.get(doc));
        let allSynced = true;
        let someSyncing = false;
        let someOffline = false;
        let someDownload = false;
        selectedSynced.forEach((synced) => {
            allSynced = allSynced && synced === Synced.Synced;
            if (synced === Synced.Syncing) {
                someSyncing = true;
            } else if (synced === Synced.Offline || (!synced && !online)) {
                someOffline = true;
            } else if (synced === Synced.Download) {
                someDownload = true;
            }
        });
        let synced: Synced = Synced.Online;
        if (allSynced) {
            synced = Synced.Synced;
        } else if (someSyncing) {
            synced = Synced.Syncing;
        } else if (someOffline) {
            synced = Synced.Offline;
        } else if (someDownload) {
            synced = Synced.Download;
        }
        return synced;
    }

    syncStats() {
        this.statsService.statsObserver.subscribe((stat) => {
            this.socket.emit('stats', stat);
        });
    }

    async waitForSynced(documentsIds: string[]) {
        try {
            await firstValueFrom(
                this.synced$.pipe(
                    filter((synced) => {
                        const selectedSynced = documentsIds.map((doc) => synced?.synced?.get(doc));
                        return selectedSynced.every((el) => el === Synced.Synced);
                    }),
                    timeout(60000),
                )
            );
        } catch (error) {
            if (error instanceof TimeoutError) {
                logger.error('Documents did not sync within one minute.');
                throw new Error('Sync timeout: Documents did not sync within one minute.');
            } else {
                throw error;
            }
        }
    }
}
